import { getFirestore, collection, doc, setDoc, serverTimestamp, deleteDoc, getDocs, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useOrganizationStore } from '../store/organization.store';

// Helper to get the current org ID
const getCurrentOrgId = () => {
  return useOrganizationStore.getState().currentOrgId;
};

// Helper to get the playlists collection path based on mode
const getPlaylistsPath = (mode) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  if (mode === 'public') {
    return `users/${user.uid}/publicPlaylists`;
  }

  const orgId = getCurrentOrgId();
  if (!orgId) throw new Error('No organization selected');
  return `organizations/${orgId}/playlists`;
};

export const addPlaylist = async (name, description = '', mode) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const db = getFirestore();
  const playlistsPath = getPlaylistsPath(mode);
  const playlistRef = doc(collection(db, playlistsPath));

  await setDoc(playlistRef, {
    name,
    description,
    userId: user.uid,
    createdAt: serverTimestamp(),
    sharedWith: []
  });
  return playlistRef.id;
};

export const deletePlaylist = async (playlistId, mode) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const db = getFirestore();
  const playlistsPath = getPlaylistsPath(mode);
  const playlistRef = doc(db, playlistsPath, playlistId);
  const tracksCol = collection(db, playlistsPath, playlistId, 'tracks');

  // Delete all tracks in the playlist
  const tracksSnap = await getDocs(tracksCol);
  const deletePromises = tracksSnap.docs.map(trackDoc => deleteDoc(trackDoc.ref));
  await Promise.all(deletePromises);

  // Delete the playlist document itself
  await deleteDoc(playlistRef);
};

export const addTrackToPlaylist = async (playlistId, trackId, mode) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const db = getFirestore();
  const playlistsPath = getPlaylistsPath(mode);
  const trackRef = doc(db, playlistsPath, playlistId, 'tracks', trackId);

  await setDoc(trackRef, {
    addedAt: serverTimestamp()
  });
};

export const removeTrackFromPlaylist = async (playlistId, trackId, mode) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const db = getFirestore();
  const playlistsPath = getPlaylistsPath(mode);
  const trackRef = doc(db, playlistsPath, playlistId, 'tracks', trackId);

  await deleteDoc(trackRef);
};

export const updatePlaylistName = async (playlistId, name, mode) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const db = getFirestore();
  const playlistsPath = getPlaylistsPath(mode);
  const playlistRef = doc(db, playlistsPath, playlistId);

  await updateDoc(playlistRef, { name });
};

export const clearPlaylistTracks = async (playlistId, mode) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const db = getFirestore();
  const playlistsPath = getPlaylistsPath(mode);
  const tracksCol = collection(db, playlistsPath, playlistId, 'tracks');

  const tracksSnap = await getDocs(tracksCol);
  const deletePromises = tracksSnap.docs.map(trackDoc => deleteDoc(trackDoc.ref));
  await Promise.all(deletePromises);
};
